import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightStartOnRectangleIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAccountPlan } from '../../hooks/useAccountUsage';
import { useFlag } from '../../hooks/useFeatureFlags';
import { useProfile } from '../../hooks/useProfile';
import { useTheme } from '../../contexts/ThemeContext';
import PlanBadge from '../ui/PlanBadge';

type ThemePreference = 'light' | 'dark' | 'system';

const THEME_OPTIONS: { value: ThemePreference; label: string; Icon: typeof SunIcon }[] = [
  { value: 'light', label: 'Light', Icon: SunIcon },
  { value: 'dark', label: 'Dark', Icon: MoonIcon },
  { value: 'system', label: 'System', Icon: ComputerDesktopIcon },
];

/**
 * Theme control — an explicit Light / Dark / System segmented control (task
 * 626), replacing the former blind cycling icon. This is the one display
 * preference kept in the menu because it is the control people genuinely flip
 * from anywhere; JSON-expand and wide-layout moved to Settings → Preferences.
 */
function ThemeSegmentedControl() {
  const { themePreference, setThemePreference } = useTheme();
  return (
    <div className="px-4 py-2 flex items-center justify-between gap-4">
      <span className="text-sm text-gray-700 dark:text-gray-300 select-none">Theme</span>
      <div
        role="group"
        aria-label="Theme"
        data-testid="user-menu-theme-control"
        className="inline-flex rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden"
      >
        {THEME_OPTIONS.map(({ value, label, Icon }) => {
          const active = themePreference === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => setThemePreference(value)}
              data-testid={`user-menu-theme-${value}`}
              aria-pressed={active}
              title={label}
              className={`flex items-center justify-center px-2.5 py-1 transition-colors ${
                active
                  ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="sr-only">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * UserMenu — avatar dropdown in the navbar. With the Settings shell (task 625)
 * giving every concern its own page, the menu is slimmed (task 626) to
 * identity · door · exit:
 *
 *   - identity header (unchanged): account slug + email + role/plan badges
 *   - **Settings** — the single door into the `/settings` shell (→ Profile)
 *   - **Billing** — a direct shortcut kept because it is the most common
 *     destination and the Stripe return path; gated by `innovate.fe.billing.page`
 *     and owner-only, mirroring the shell's owner-gated Billing group
 *   - Theme — an explicit Light / Dark / System segmented control
 *   - Sign out
 *
 * LLM Configs and MCP Connections were account *resources*, not personal
 * settings, and moved to the Advanced nav dropdown (task 626). Email & Privacy,
 * JSON-expand, and wide-layout moved into the Settings shell's pages.
 *
 * The avatar shows the User's AMS `initials` read through the profile proxy
 * (task 1089) — the same stored mark that signs their Build comments, set or
 * cleared on the Profile page. Nothing about an avatar is uploaded.
 */
export default function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuthContext();
  const billingEnabled = useFlag('innovate.fe.billing.page');
  const { data: profile } = useProfile();
  // Plan comes from GET /api/accounts/usage (task 676), not the JWT.
  const { plan } = useAccountPlan();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!user) return null;

  // Billing is owner-only, mirroring the Settings shell's owner-gated Billing
  // group (task 625). The owner check now lives in the flag's role-owner
  // cohort (task 692), so the flag alone decides.
  const showBilling = billingEnabled;

  // The plan badge deep-links to the plan cards at Settings → Subscription
  // (task 1067). The Billing entry below stays on `/settings/billing`: it
  // means invoices.
  const planBadgeTarget = '/settings/subscription';

  // Initials are the AMS `initials` from the profile (task 1089), not token
  // claims: a value set on the Profile page must show up here. Until the
  // profile has loaded, fall back to the email initial so the avatar never
  // flashes empty.
  const initials = profile
    ? profile.initials.toUpperCase()
    : (user.email?.charAt(0) ?? '?').toUpperCase();

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen((o) => !o)}
        data-testid="user-menu-button"
        className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition-colors"
        aria-label="User menu"
        title={user.email}
      >
        {initials}
      </button>

      {isOpen && (
        <div
          data-testid="user-menu-panel"
          className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1"
        >
          <div className="px-4 py-2.5">
            {/* Account identity (task 383). The slug is the User's visible
                account handle — it is the `{accountSlug}` segment of every
                endpoint URL they publish, so it belongs above the email.
                Absent on tokens minted before the claim shipped, and null for
                a user with no membership — both render nothing. */}
            {user.accountSlug && (
              <p
                className="text-sm font-semibold text-gray-900 dark:text-white truncate font-mono"
                data-testid="user-menu-account-slug"
                title={`Account: ${user.accountSlug}`}
              >
                {user.accountSlug}
              </p>
            )}
            <p
              className={`text-sm truncate ${
                user.accountSlug
                  ? 'text-gray-500 dark:text-gray-400'
                  : 'font-medium text-gray-900 dark:text-white'
              }`}
            >
              {user.email}
            </p>
            <div className="mt-1 flex items-center gap-2">
              {user.accountRole && (
                <span className="inline-block px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 text-xs font-medium">
                  {user.accountRole}
                </span>
              )}
              {/* The no-plan variant of PlanBadge is its own anchor to
                  /settings/subscription (task 466), so only wrap it in the router
                  Link when a plan exists — anchors must not nest. */}
              {showBilling && plan ? (
                <Link
                  to={planBadgeTarget}
                  onClick={() => setIsOpen(false)}
                  className="inline-flex hover:opacity-80 transition-opacity"
                  title="Plan &amp; Billing"
                  data-testid="plan-badge-link"
                >
                  <PlanBadge />
                </Link>
              ) : (
                <PlanBadge />
              )}
            </div>
          </div>
          <hr className="my-1 border-gray-200 dark:border-gray-700" />
          <Link
            to="/settings/profile"
            onClick={() => setIsOpen(false)}
            data-testid="user-menu-settings-link"
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Cog6ToothIcon className="h-4 w-4 text-gray-400" />
            Settings
          </Link>
          {showBilling && (
            <Link
              to="/settings/billing"
              onClick={() => setIsOpen(false)}
              data-testid="user-menu-billing-link"
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <CreditCardIcon className="h-4 w-4 text-gray-400" />
              Billing
            </Link>
          )}
          <hr className="my-1 border-gray-200 dark:border-gray-700" />
          <ThemeSegmentedControl />
          <hr className="my-1 border-gray-200 dark:border-gray-700" />
          <button
            onClick={() => {
              setIsOpen(false);
              logout();
            }}
            data-testid="user-menu-signout"
            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowRightStartOnRectangleIcon className="h-4 w-4 text-gray-400" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
